import { useEffect, useState } from "react";

/**
 * One Conscious Breath — a small ceremonial button that invites the visitor to
 * cross the threshold before exploring. On press, dispatches `kaf:breath` so
 * the Living Frequency Field responds (calming pass, wide golden ripple,
 * heart pulse). Uses body-level `data-breath="active"` for a brief brightening
 * of hero + nav via CSS transitions.
 *
 * Non-blocking. Skippable. Once taken, the prompt fades to a quiet
 * "breathe again" link so it never nags.
 */

const KEY = "kaf:breath-taken";
const DURATION_MS = 4200;

export function ConsciousBreath() {
  const [taken, setTaken] = useState(false);
  const [active, setActive] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try { if (window.localStorage.getItem(KEY) === "1") setTaken(true); } catch { /* noop */ }
  }, []);

  const begin = () => {
    if (active) return;
    setActive(true);
    try { document.body.setAttribute("data-breath", "active"); } catch { /* noop */ }
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight * 0.45;
    window.dispatchEvent(new CustomEvent("kaf:breath", { detail: { x: cx, y: cy } }));
    try { window.localStorage.setItem(KEY, "1"); } catch { /* noop */ }
    window.setTimeout(() => {
      setActive(false);
      setTaken(true);
      try { document.body.removeAttribute("data-breath"); } catch { /* noop */ }
    }, DURATION_MS);
  };

  if (dismissed && taken) return null;

  return (
    <div className="mt-8 flex flex-col items-center gap-2">
      {!taken ? (
        <>
          <button
            type="button"
            onClick={begin}
            disabled={active}
            aria-live="polite"
            className={`group inline-flex items-center gap-3 rounded-full border border-gold/60 bg-obsidian/40 px-6 py-3 text-sm tracking-[0.2em] text-gold-luminous backdrop-blur transition-all hover:border-gold hover:bg-gold/10 disabled:opacity-70 ${
              active ? "shadow-halo" : ""
            }`}
          >
            <span
              aria-hidden
              className={`inline-block h-2.5 w-2.5 rounded-full bg-gold-gradient ${
                active ? "animate-[pulse_4s_ease-in-out_1]" : "animate-[pulse_6s_ease-in-out_infinite]"
              }`}
            />
            {active ? "Breathing…" : "Take One Conscious Breath"}
          </button>
          <button
            type="button"
            onClick={() => { setDismissed(true); setTaken(true); try { window.localStorage.setItem(KEY, "1"); } catch { /* noop */ } }}
            className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 hover:text-gold"
          >
            Skip the threshold
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={begin}
          disabled={active}
          className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground hover:text-gold-luminous"
        >
          {active ? "Breathing…" : "Breathe again"}
        </button>
      )}
    </div>
  );
}
